import { useMemo } from 'react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { StoneXGrid } from '../../../StoneX';
import { StoneXNumber } from '../../../StoneXMui';
import { numberToMonth } from '../../../../Helpers/MonthHelper';
import { MatrixForwardCurveSeries, MatrixTermResult } from './MatrixTypes';

interface MatrixSettlementTableProps {
  results: MatrixTermResult[];
  forwardCurveSeries: MatrixForwardCurveSeries[];
  selectedPercentiles: number[];
}

// Group terms into logical tables
const TERM_GROUPS: Record<string, string[]> = {
  annual: ['Annual'],
  seasonal: ['Summer', 'Winter'],
  quarterly: ['Q1', 'Q2', 'Q3', 'Q4'],
  bimonthly: ['January-February', 'March-April', 'July-August', 'October-December'],
};

function getTermGroup(termName: string): string {
  for (const [group, terms] of Object.entries(TERM_GROUPS)) {
    if (terms.includes(termName)) {
      return group;
    }
  }
  return termName;
}

interface SettlementRow {
  contractLabel: string;
  closingPrice: number | null;
  isHeader?: boolean;
  termName?: string;
  [key: string]: any;
}

interface TermGroupTable {
  groupLabel: string;
  terms: string[];
  headerRows: SettlementRow[];
  dataRows: SettlementRow[];
}

export default function MatrixSettlementTable({ results, forwardCurveSeries, selectedPercentiles }: MatrixSettlementTableProps) {
  const sortedDeciles = useMemo(() => [...selectedPercentiles].filter(d => d > 0 && d < 100).sort((a, b) => b - a), [selectedPercentiles]);

  const tables = useMemo<TermGroupTable[]>(() => {
    if (forwardCurveSeries.length === 0 || results.length === 0) { return []; }

    // Map term results by name
    const resultsByTerm = new Map<string, MatrixTermResult>();
    for (const result of results) {
      resultsByTerm.set(result.season, result);
    }

    // Map forward curve by term
    const curveByTerm = new Map<string, MatrixForwardCurveSeries>();
    for (const curve of forwardCurveSeries) {
      curveByTerm.set(curve.term, curve);
    }

    // Group terms
    const termGroups = new Map<string, string[]>();
    for (const result of results) {
      const group = getTermGroup(result.season);
      if (!termGroups.has(group)) {
        termGroups.set(group, []);
      }
      termGroups.get(group)!.push(result.season);
    }

    const output: TermGroupTable[] = [];

    const groupEntries = Array.from(termGroups.entries());
    for (const [group, terms] of groupEntries) {
      // Build header rows: one per term showing decile values
      const headerRows: SettlementRow[] = terms.map((termName: string) => {
        const termResult = resultsByTerm.get(termName);
        const row: SettlementRow = {
          contractLabel: termName.toUpperCase(),
          closingPrice: null,
          isHeader: true,
          termName: termName,
        };
        if (termResult) {
          for (const decile of sortedDeciles) {
            const decileEntry = termResult.weightedDeciles.find(d => Math.abs(d.rank - decile / 100) < 0.001);
            row[`d${decile}`] = decileEntry?.value ?? null;
          }
        }
        return row;
      });

      // Build data rows: each contract month from all terms in this group
      const dataRows: SettlementRow[] = [];
      for (const termName of terms) {
        const curve = curveByTerm.get(termName);
        const termResult = resultsByTerm.get(termName);
        if (!curve || !termResult) { continue; }

        for (const point of curve.prices) {
          const row: SettlementRow = {
            contractLabel: `${numberToMonth(point.contractMonth) ?? ''}, ${point.contractYear}`,
            closingPrice: point.price,
          };

          for (const decile of sortedDeciles) {
            const decileEntry = termResult.weightedDeciles.find(d => Math.abs(d.rank - decile / 100) < 0.001);
            const decileValue = decileEntry?.value ?? null;
            row[`d${decile}`] = decileValue != null ? point.price - decileValue : null;
          }

          dataRows.push(row);
        }
      }

      if (dataRows.length > 0) {
        output.push({
          groupLabel: terms.length === 1 ? terms[0] : terms.join(' / '),
          terms,
          headerRows,
          dataRows,
        });
      }
    }

    return output;
  }, [results, forwardCurveSeries, sortedDeciles]);

  const columns = useMemo<ColDef<SettlementRow>[]>(() => {
    const cols: ColDef<SettlementRow>[] = [
      {
        headerName: '',
        field: 'contractLabel',
        flex: 1.5,
        pinned: 'left',
        cellRenderer: (params: ICellRendererParams<SettlementRow>) => {
            if (params.data?.isHeader) {
            return <span className='bold'>{params.value}</span>;
          }
          return <span>{params.value}</span>;
        },
      },
      {
        headerName: 'Closing Price',
        field: 'closingPrice',
        flex: 1,
        cellRenderer: (params: ICellRendererParams<SettlementRow>) => {
          if (params.data?.isHeader) { return <span></span>; }
          return <StoneXNumber number={params.value ?? undefined} decimals={3} />;
        },
      },
    ];

    for (const decile of sortedDeciles) {
      cols.push({
        headerName: `+/- ${decile}th Decile`,
        field: `d${decile}`,
        flex: 1,
        cellRenderer: (params: ICellRendererParams<SettlementRow>) => {
          const value = params.value as number | null;
          if (params.data?.isHeader) {
            return <StoneXNumber number={value ?? undefined} decimals={3} />;
          }
          return <StoneXNumber number={value ?? undefined} decimals={3} colored />;
        },
      });
    }

    return cols;
  }, [sortedDeciles]);

  if (tables.length === 0) { return null; }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {tables.map((table, index) => (
        <div key={index}>
          <h4 style={{ margin: '0 0 4px 8px', color: 'var(--font-color)' }}>{table.groupLabel}</h4>
          <StoneXGrid
            rows={[...table.headerRows, ...table.dataRows]}
            columns={columns}
            hideGridOptions
          />
        </div>
      ))}
    </div>
  );
}
